import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, UserPlus, Trash2, Shield, 
  AlertCircle, CheckCircle, Mail, User, DollarSign,
  CreditCard, Plus, Eye, EyeOff, Search, Filter,
  TrendingUp, TrendingDown, RefreshCw, Home, Activity,
  Settings, List, Grid, AlertTriangle, Wallet, Clock,
  Check, X, FileText, Send, Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, authAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [userAccounts, setUserAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user'
  });
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    operation: 'add',
    reason: ''
  });
  const [newAccount, setNewAccount] = useState({
    account_number: '',
    iban: '',
    balance: '',
    currency: 'USD',
    bank_name: 'CYBERBANK TERMINAL',
    swift_code: 'CYBRBKXX'
  });
  const [approvalForm, setApprovalForm] = useState({
    approved: true,
    admin_notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
    loadPendingTransactions();
  }, [user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadUserAccounts = async (userId) => {
    try {
      setLoadingAccounts(true);
      const response = await adminAPI.getUserAccounts(userId);
      setUserAccounts(response.data);
    } catch (error) {
      console.error('Error loading user accounts:', error);
      setUserAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadPendingTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await adminAPI.getAllTransactions(0, 100);
      // Filter only pending transactions
      const pending = response.data.filter(txn => txn.status === 'PENDING');
      setPendingTransactions(pending);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Detect and filter duplicate users
  const { uniqueUsers, duplicateCount } = useMemo(() => {
    const seen = new Map();
    const unique = [];
    let dupes = 0;

    users.forEach(usr => {
      const key = usr.username.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, usr);
        unique.push(usr);
      } else {
        dupes++;
      }
    });

    return { uniqueUsers: unique, duplicateCount: dupes };
  }, [users]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return uniqueUsers;
    const query = searchQuery.toLowerCase();
    return uniqueUsers.filter(usr => 
      usr.username.toLowerCase().includes(query) ||
      usr.email?.toLowerCase().includes(query) ||
      usr.full_name?.toLowerCase().includes(query)
    );
  }, [uniqueUsers, searchQuery]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await authAPI.register(newUser);
      setSuccess('User created successfully');
      setNewUser({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'user'
      });
      setShowCreateModal(false);
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user: ${username}?`)) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      setSuccess(`User ${username} deleted successfully`);
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleExpandUser = async (usr) => {
    if (expandedUser === usr.id) {
      setExpandedUser(null);
      setUserAccounts([]);
    } else {
      setExpandedUser(usr.id);
      await loadUserAccounts(usr.id);
    }
  };

  const handleOpenBalanceModal = (account, usr) => {
    setSelectedAccount(account);
    setSelectedUser(usr);
    setBalanceForm({ amount: '', operation: 'add', reason: '' });
    setShowBalanceModal(true);
  };

  const handleBalanceUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!balanceForm.reason.trim()) {
      setError('Please provide a reason for this operation');
      return;
    }

    try {
      const response = await adminAPI.updateAccountBalance(selectedAccount.id, {
        amount: parseFloat(balanceForm.amount),
        operation: balanceForm.operation,
        reason: balanceForm.reason
      });

      setSuccess(response.data.message);
      setShowBalanceModal(false);
      setBalanceForm({ amount: '', operation: 'add', reason: '' });
      
      // Refresh user accounts
      if (selectedUser) {
        await loadUserAccounts(selectedUser.id);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update balance');
    }
  };

  const handleOpenAccountModal = (usr) => {
    setSelectedUser(usr);
    const timestamp = Date.now().toString(36).toUpperCase();
    setNewAccount({
      account_number: `CB${timestamp}`,
      iban: `CB00000000${timestamp}`,
      balance: '',
      currency: 'USD',
      bank_name: 'CYBERBANK TERMINAL',
      swift_code: 'CYBRBKXX'
    });
    setShowAccountModal(true);
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await adminAPI.createUserAccount(selectedUser.id, {
        ...newAccount,
        balance: parseFloat(newAccount.balance) || 0
      });

      setSuccess(`Account created for ${selectedUser.username}`);
      setShowAccountModal(false);
      
      // Refresh user accounts
      await loadUserAccounts(selectedUser.id);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleOpenApprovalModal = (transaction, isApprove) => {
    setSelectedTransaction(transaction);
    setApprovalForm({
      approved: isApprove,
      admin_notes: ''
    });
    setShowApprovalModal(true);
  };

  const handleApproveTransaction = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await adminAPI.approveTransaction(selectedTransaction.id, {
        transaction_id: selectedTransaction.id,
        approved: approvalForm.approved,
        admin_notes: approvalForm.admin_notes
      });

      setSuccess(response.data.message);
      setShowApprovalModal(false);
      setApprovalForm({ approved: true, admin_notes: '' });
      
      // Refresh pending transactions
      await loadPendingTransactions();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to process transaction');
    }
  };

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: 'DASHBOARD', icon: Home, path: '/dashboard' },
    { id: 'users', label: 'USERS', icon: Users, active: activeTab === 'users', onClick: () => setActiveTab('users') },
    { id: 'transfers', label: 'TRANSFERS', icon: Send, active: activeTab === 'transfers', onClick: () => setActiveTab('transfers'), badge: pendingTransactions.length },
    { id: 'accounts', label: 'ACCOUNTS', icon: CreditCard, path: '/account-management' },
    { id: 'transactions', label: 'HISTORY', icon: Activity, path: '/transactions' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Shield className="w-12 h-12 text-terminal-green" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-terminal-green">
      <div className="scanline" />
      
      {/* Sidebar Navigation */}
      <div className="fixed left-0 top-0 h-full w-16 md:w-56 bg-black border-r border-terminal-green/30 z-40">
        <div className="p-4 border-b border-terminal-green/30">
          <div className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-terminal-green" />
            <span className="hidden md:block text-lg font-bold cyber-font">ADMIN</span>
          </div>
        </div>
        
        <nav className="p-2 space-y-1">
          {navItems.map((item) => (
            <motion.button
              key={item.id}
              whileHover={{ x: 3 }}
              onClick={() => item.path ? navigate(item.path) : item.onClick?.()}
              className={`w-full flex items-center gap-3 p-3 rounded transition-all relative ${
                item.active 
                  ? 'bg-terminal-green/20 border border-terminal-green' 
                  : 'hover:bg-terminal-green/10 border border-transparent'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="hidden md:block text-sm">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </motion.button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-16 md:ml-56 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Alerts */}
          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="mb-6 p-4 border border-terminal-green bg-terminal-green/10 flex items-center gap-2"
                data-testid="success-message"
              >
                <CheckCircle className="w-5 h-5 text-terminal-green" />
                <span>{success}</span>
                <button onClick={() => setSuccess('')} className="ml-auto">x</button>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="mb-6 p-4 border border-red-500 bg-red-500/10 flex items-center gap-2"
                data-testid="error-message"
              >
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-500">{error}</span>
                <button onClick={() => setError('')} className="ml-auto text-red-500">x</button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2">
                      USER MANAGEMENT
                    </h1>
                    <p className="text-terminal-green-dim text-sm">
                      Manage users, accounts, and balances
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={loadUsers}
                      className="terminal-button px-4 py-2 flex items-center gap-2"
                      data-testid="refresh-button"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="hidden md:inline">REFRESH</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCreateModal(true)}
                      className="terminal-button px-6 py-2 flex items-center gap-2"
                      data-testid="create-user-button"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>CREATE USER</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {duplicateCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-6 p-4 border border-yellow-500 bg-yellow-500/10 flex items-center gap-2"
                >
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <span className="text-yellow-500">
                    {duplicateCount} duplicate entries detected and filtered out
                  </span>
                </motion.div>
              )}

              {/* Search and Filter Bar */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="mb-6 flex flex-col md:flex-row gap-4"
              >
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-terminal-green-dim" />
                  <input
                    type="text"
                    placeholder="Search users by username, email, or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 terminal-input"
                    data-testid="search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-3 border ${viewMode === 'list' ? 'border-terminal-green bg-terminal-green/20' : 'border-terminal-green/50'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-3 border ${viewMode === 'grid' ? 'border-terminal-green bg-terminal-green/20' : 'border-terminal-green/50'}`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>

              {/* Users List */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="terminal-card p-6"
              >
                <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
                  <Users className="w-5 h-5" />
                  <h2 className="text-xl font-bold cyber-font">ALL USERS</h2>
                  <span className="text-xs px-2 py-1 border border-terminal-green">
                    {filteredUsers.length}
                  </span>
                </div>

                <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-3'}>
                  {filteredUsers.map((usr, index) => (
                    <motion.div
                      key={usr.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.03 }}
                      className="border border-terminal-green/50 hover:border-terminal-green transition-all"
                      data-testid={`user-card-${index}`}
                    >
                      {/* User Header */}
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => handleExpandUser(usr)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                              usr.role === 'admin' ? 'border-terminal-green bg-terminal-green/20' : 'border-terminal-green/50'
                            }`}>
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="font-bold uppercase flex items-center gap-2">
                                {usr.username}
                                {usr.role === 'admin' && (
                                  <Shield className="w-4 h-4 text-terminal-green" />
                                )}
                              </div>
                              <div className="text-xs text-terminal-green-dim">
                                {usr.full_name || 'No name provided'}
                              </div>
                              {usr.email && (
                                <div className="text-xs text-terminal-green-dim flex items-center gap-1 mt-1">
                                  <Mail className="w-3 h-3" />
                                  {usr.email}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-3 py-1 border ${
                              usr.role === 'admin' 
                                ? 'border-terminal-green text-terminal-green' 
                                : 'border-terminal-green/50 text-terminal-green-dim'
                            }`}>
                              {usr.role.toUpperCase()}
                            </span>
                            
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              className="text-terminal-green-dim hover:text-terminal-green"
                            >
                              {expandedUser === usr.id ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </motion.button>
                            
                            {usr.role !== 'admin' && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => { e.stopPropagation(); handleDeleteUser(usr.id, usr.username); }}
                                className="text-red-500 hover:text-red-400"
                                data-testid={`delete-user-${index}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </motion.button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded User Details - Accounts */}
                      <AnimatePresence>
                        {expandedUser === usr.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-terminal-green/30 overflow-hidden"
                          >
                            <div className="p-4 bg-terminal-green/5">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 text-sm font-bold">
                                  <Wallet className="w-4 h-4" />
                                  USER ACCOUNTS
                                </div>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={(e) => { e.stopPropagation(); handleOpenAccountModal(usr); }}
                                  className="text-xs px-3 py-1 border border-terminal-green hover:bg-terminal-green/20 flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  NEW ACCOUNT
                                </motion.button>
                              </div>

                              {loadingAccounts ? (
                                <div className="text-center py-4 text-terminal-green-dim">
                                  Loading accounts...
                                </div>
                              ) : userAccounts.length > 0 ? (
                                <div className="space-y-2">
                                  {userAccounts.map((account, accIndex) => (
                                    <div
                                      key={account.id}
                                      className="p-3 border border-terminal-green/30 bg-black/50"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="text-xs text-terminal-green-dim">ACCOUNT</div>
                                          <div className="font-mono text-sm">{account.account_number}</div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-xs text-terminal-green-dim">BALANCE</div>
                                          <div className="font-bold">
                                            ${account.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="flex gap-2 mt-3">
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setBalanceForm({ ...balanceForm, operation: 'add' });
                                            handleOpenBalanceModal(account, usr); 
                                          }}
                                          className="flex-1 py-2 px-3 border border-terminal-green hover:bg-terminal-green/20 flex items-center justify-center gap-1 text-xs"
                                        >
                                          <TrendingUp className="w-3 h-3" />
                                          ADD FUNDS
                                        </motion.button>
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setBalanceForm({ ...balanceForm, operation: 'subtract' });
                                            handleOpenBalanceModal(account, usr); 
                                          }}
                                          className="flex-1 py-2 px-3 border border-red-500 hover:bg-red-500/20 flex items-center justify-center gap-1 text-xs text-red-500"
                                        >
                                          <TrendingDown className="w-3 h-3" />
                                          SUBTRACT
                                        </motion.button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-terminal-green-dim text-sm">
                                  No accounts found for this user
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>

                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-terminal-green-dim">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No users found</p>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {/* Pending Transfers Tab */}
          {activeTab === 'transfers' && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2">
                      PENDING TRANSFERS
                    </h1>
                    <p className="text-terminal-green-dim text-sm">
                      Review and approve/reject user transfer requests
                    </p>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={loadPendingTransactions}
                    className="terminal-button px-4 py-2 flex items-center gap-2"
                    data-testid="refresh-transfers-button"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingTransactions ? 'animate-spin' : ''}`} />
                    <span>REFRESH</span>
                  </motion.button>
                </div>
              </motion.div>

              {/* Pending Transfers List */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="terminal-card p-6"
              >
                <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <h2 className="text-xl font-bold cyber-font">AWAITING APPROVAL</h2>
                  <span className="text-xs px-2 py-1 border border-yellow-500 text-yellow-500">
                    {pendingTransactions.length}
                  </span>
                </div>

                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="inline-block"
                    >
                      <RefreshCw className="w-8 h-8 text-terminal-green" />
                    </motion.div>
                    <p className="mt-2 text-terminal-green-dim">Loading transfers...</p>
                  </div>
                ) : pendingTransactions.length > 0 ? (
                  <div className="space-y-4">
                    {pendingTransactions.map((txn, index) => (
                      <motion.div
                        key={txn.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border border-yellow-500/50 p-4 bg-yellow-500/5"
                        data-testid={`pending-transfer-${index}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Clock className="w-5 h-5 text-yellow-500" />
                              <span className="font-bold text-yellow-500">PENDING</span>
                              <span className="text-xs px-2 py-0.5 border border-terminal-green/50">
                                {txn.transfer_type}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-xs text-terminal-green-dim">REFERENCE</div>
                                <div className="font-mono">{txn.transaction_reference}</div>
                              </div>
                              <div>
                                <div className="text-xs text-terminal-green-dim">AMOUNT</div>
                                <div className="font-bold text-lg">${txn.amount.toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-xs text-terminal-green-dim">RECIPIENT</div>
                                <div className="truncate">{txn.recipient_info || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-xs text-terminal-green-dim">DATE</div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(txn.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleOpenApprovalModal(txn, true)}
                              className="px-4 py-2 border border-terminal-green bg-terminal-green/20 hover:bg-terminal-green/30 flex items-center gap-2"
                              data-testid={`approve-transfer-${index}`}
                            >
                              <Check className="w-4 h-4" />
                              <span>APPROVE</span>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleOpenApprovalModal(txn, false)}
                              className="px-4 py-2 border border-red-500 text-red-500 hover:bg-red-500/20 flex items-center gap-2"
                              data-testid={`reject-transfer-${index}`}
                            >
                              <X className="w-4 h-4" />
                              <span>REJECT</span>
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-terminal-green-dim">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No pending transfers</p>
                    <p className="text-sm mt-1">All transfer requests have been processed</p>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="terminal-card p-8 w-full max-w-md"
            >
              <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
                <UserPlus className="w-5 h-5" />
                <h2 className="text-xl font-bold cyber-font">CREATE NEW USER</h2>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">USERNAME *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    className="w-full px-4 py-3 terminal-input uppercase"
                    required
                    data-testid="new-username-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">PASSWORD *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    required
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">FULL NAME</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    data-testid="new-fullname-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">EMAIL</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    data-testid="new-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">ROLE</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    data-testid="new-role-select"
                  >
                    <option value="user">USER</option>
                    <option value="admin">ADMIN</option>
                  </select>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 terminal-button py-3"
                    data-testid="submit-create-user"
                  >
                    CREATE
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 terminal-button py-3"
                    data-testid="cancel-create-user"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Balance Update Modal */}
      <AnimatePresence>
        {showBalanceModal && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="terminal-card p-8 w-full max-w-md"
            >
              <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
                {balanceForm.operation === 'add' ? (
                  <>
                    <TrendingUp className="w-5 h-5" />
                    <h2 className="text-xl font-bold cyber-font">ADD FUNDS</h2>
                  </>
                ) : (
                  <>
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <h2 className="text-xl font-bold cyber-font text-red-500">SUBTRACT FUNDS</h2>
                  </>
                )}
              </div>

              <div className="mb-4 p-3 border border-terminal-green/30 bg-terminal-green/5">
                <div className="text-xs text-terminal-green-dim">TARGET ACCOUNT</div>
                <div className="font-bold">{selectedAccount?.account_number}</div>
                <div className="text-sm mt-1">
                  User: {selectedUser?.username}
                </div>
                <div className="text-sm">
                  Current Balance: ${selectedAccount?.balance.toLocaleString()}
                </div>
              </div>

              <form onSubmit={handleBalanceUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">OPERATION</label>
                  <select
                    value={balanceForm.operation}
                    onChange={(e) => setBalanceForm({...balanceForm, operation: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                  >
                    <option value="add">ADD FUNDS</option>
                    <option value="subtract">SUBTRACT FUNDS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">AMOUNT ({selectedAccount?.currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={balanceForm.amount}
                    onChange={(e) => setBalanceForm({...balanceForm, amount: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    placeholder="0.00"
                    required
                    data-testid="balance-amount-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">REASON / NOTES *</label>
                  <textarea
                    value={balanceForm.reason}
                    onChange={(e) => setBalanceForm({...balanceForm, reason: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    rows="3"
                    placeholder="Provide a reason for this operation..."
                    required
                    data-testid="balance-reason-input"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className={`flex-1 terminal-button py-3 ${balanceForm.operation === 'subtract' ? 'border-red-500 text-red-500 hover:bg-red-500/20' : ''}`}
                    data-testid="submit-balance-update"
                  >
                    {balanceForm.operation === 'add' ? 'ADD FUNDS' : 'SUBTRACT FUNDS'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBalanceModal(false);
                      setBalanceForm({ amount: '', operation: 'add', reason: '' });
                    }}
                    className="flex-1 terminal-button py-3"
                    data-testid="cancel-balance-update"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Create Account Modal */}
      <AnimatePresence>
        {showAccountModal && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="terminal-card p-8 w-full max-w-md"
            >
              <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
                <CreditCard className="w-5 h-5" />
                <h2 className="text-xl font-bold cyber-font">CREATE ACCOUNT</h2>
              </div>

              <div className="mb-4 p-3 border border-terminal-green/30 bg-terminal-green/5">
                <div className="text-xs text-terminal-green-dim">FOR USER</div>
                <div className="font-bold">{selectedUser?.username}</div>
              </div>

              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">ACCOUNT NUMBER *</label>
                  <input
                    type="text"
                    value={newAccount.account_number}
                    onChange={(e) => setNewAccount({...newAccount, account_number: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    required
                    data-testid="account-number-input"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">IBAN</label>
                  <input
                    type="text"
                    value={newAccount.iban}
                    onChange={(e) => setNewAccount({...newAccount, iban: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    data-testid="account-iban-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">INITIAL BALANCE</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newAccount.balance}
                      onChange={(e) => setNewAccount({...newAccount, balance: e.target.value})}
                      className="w-full px-4 py-3 terminal-input"
                      placeholder="0.00"
                      data-testid="account-balance-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">CURRENCY</label>
                    <select
                      value={newAccount.currency}
                      onChange={(e) => setNewAccount({...newAccount, currency: e.target.value})}
                      className="w-full px-4 py-3 terminal-input"
                      data-testid="account-currency-select"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-2">BANK NAME</label>
                  <input
                    type="text"
                    value={newAccount.bank_name}
                    onChange={(e) => setNewAccount({...newAccount, bank_name: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    data-testid="account-bank-input"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 terminal-button py-3"
                    data-testid="submit-create-account"
                  >
                    CREATE ACCOUNT
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAccountModal(false)}
                    className="flex-1 terminal-button py-3"
                    data-testid="cancel-create-account"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Transaction Approval Modal */}
      <AnimatePresence>
        {showApprovalModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="terminal-card p-8 w-full max-w-md"
            >
              <div className="flex items-center gap-2 mb-6 border-b border-terminal-green pb-3">
                {approvalForm.approved ? (
                  <>
                    <Check className="w-5 h-5 text-terminal-green" />
                    <h2 className="text-xl font-bold cyber-font">APPROVE TRANSFER</h2>
                  </>
                ) : (
                  <>
                    <X className="w-5 h-5 text-red-500" />
                    <h2 className="text-xl font-bold cyber-font text-red-500">REJECT TRANSFER</h2>
                  </>
                )}
              </div>

              <div className="mb-4 p-4 border border-terminal-green/30 bg-terminal-green/5 space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-terminal-green-dim">REFERENCE</span>
                  <span className="font-mono text-sm">{selectedTransaction.transaction_reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-terminal-green-dim">TYPE</span>
                  <span className="text-sm">{selectedTransaction.transfer_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-terminal-green-dim">AMOUNT</span>
                  <span className="font-bold text-lg">${selectedTransaction.amount.toLocaleString()} {selectedTransaction.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-terminal-green-dim">RECIPIENT</span>
                  <span className="text-sm">{selectedTransaction.recipient_info || 'N/A'}</span>
                </div>
              </div>

              {approvalForm.approved ? (
                <div className="mb-4 p-3 border border-terminal-green bg-terminal-green/10 text-sm">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    This will deduct ${selectedTransaction.amount.toLocaleString()} from the user's account
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 border border-red-500 bg-red-500/10 text-sm text-red-500">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    This transfer will be cancelled and no funds will be deducted
                  </div>
                </div>
              )}

              <form onSubmit={handleApproveTransaction} className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">ADMIN NOTES (Optional)</label>
                  <textarea
                    value={approvalForm.admin_notes}
                    onChange={(e) => setApprovalForm({...approvalForm, admin_notes: e.target.value})}
                    className="w-full px-4 py-3 terminal-input"
                    rows="3"
                    placeholder="Add notes about this decision..."
                    data-testid="approval-notes-input"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className={`flex-1 terminal-button py-3 ${!approvalForm.approved ? 'border-red-500 text-red-500 hover:bg-red-500/20' : ''}`}
                    data-testid="submit-approval"
                  >
                    {approvalForm.approved ? 'CONFIRM APPROVAL' : 'CONFIRM REJECTION'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowApprovalModal(false);
                      setApprovalForm({ approved: true, admin_notes: '' });
                    }}
                    className="flex-1 terminal-button py-3"
                    data-testid="cancel-approval"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
